import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

export type MarketingLeadAccountCard = {
  id: string;
  title: string;
  companyName: string | null;
  domain: string | null;
};

export type MarketingLead = {
  id: string;
  email: string;
  preferredEmail: string | null;
  source: string | null;
  createdAt: string;
  accountCard: MarketingLeadAccountCard | null;
};

export type MarketingLeadsResponse = {
  ok: boolean;
  data: MarketingLead[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

export type MarketingLeadAccountCardsResponse = {
  ok: boolean;
  data: MarketingLeadAccountCard[];
};

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

async function requestJson<T>(
  path: string,
  init: RequestInit | undefined,
  fallback: string,
  fetchImpl: FetchLike
): Promise<T> {
  const response = await fetchImpl(`${API_URL}${path}`, {
    credentials: 'include',
    ...init,
  });
  if (!response.ok) {
    throw await toApiError(response, fallback);
  }
  return (await response.json()) as T;
}

export async function fetchMarketingLeads(
  params: { page?: number; pageSize?: number; search?: string },
  fetchImpl: FetchLike = fetch
): Promise<MarketingLeadsResponse> {
  const searchParams = new URLSearchParams({
    page: String(params.page ?? 1),
    pageSize: String(params.pageSize ?? 20),
  });
  if (params.search) searchParams.set('search', params.search);

  return requestJson<MarketingLeadsResponse>(
    `/superadmin/marketing-leads?${searchParams.toString()}`,
    undefined,
    'Failed to fetch marketing leads',
    fetchImpl
  );
}

export async function fetchMarketingLeadAccountCards(
  search: string,
  fetchImpl: FetchLike = fetch
): Promise<MarketingLeadAccountCardsResponse> {
  const searchParams = new URLSearchParams();
  if (search.trim()) searchParams.set('search', search.trim());
  const query = searchParams.toString();

  return requestJson<MarketingLeadAccountCardsResponse>(
    `/superadmin/marketing-leads/account-cards${query ? `?${query}` : ''}`,
    undefined,
    'Failed to fetch account cards',
    fetchImpl
  );
}

const marketingLeadsKey = ['superadmin', 'marketing-leads'] as const;
type MarketingLeadMutationResponse = { ok: boolean; data: MarketingLead };

export function useMarketingLeadsQuery(params: {
  page: number;
  pageSize: number;
  search?: string;
}) {
  return useQuery({
    queryKey: [...marketingLeadsKey, params],
    queryFn: () => fetchMarketingLeads(params),
  });
}

export function useMarketingLeadAccountCardsQuery(search: string, enabled: boolean) {
  return useQuery({
    queryKey: [...marketingLeadsKey, 'account-cards', search],
    queryFn: () => fetchMarketingLeadAccountCards(search),
    enabled,
  });
}

export function useUpdateMarketingLeadPreferredEmailMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string; preferredEmail: string | null }) => {
      const response = await requestJson<MarketingLeadMutationResponse>(
        `/superadmin/marketing-leads/${encodeURIComponent(input.id)}`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ preferredEmail: input.preferredEmail }),
        },
        'Failed to update preferred email',
        fetch
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketingLeadsKey });
    },
  });
}

export function useLinkMarketingLeadAccountCardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { leadId: string; accountCardId: string }) => {
      const response = await requestJson<MarketingLeadMutationResponse>(
        `/superadmin/marketing-leads/${encodeURIComponent(input.leadId)}/account-card`,
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ accountCardId: input.accountCardId }),
        },
        'Failed to link account card',
        fetch
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketingLeadsKey });
    },
  });
}

export function useCreateMarketingLeadAccountCardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      leadId: string;
      title: string;
      companyName: string;
      domain: string;
    }) => {
      const response = await requestJson<MarketingLeadMutationResponse>(
        `/superadmin/marketing-leads/${encodeURIComponent(input.leadId)}/account-card`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            title: input.title,
            companyName: input.companyName,
            domain: input.domain,
          }),
        },
        'Failed to create account card',
        fetch
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketingLeadsKey });
    },
  });
}

export function useUnlinkMarketingLeadAccountCardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      const response = await requestJson<MarketingLeadMutationResponse>(
        `/superadmin/marketing-leads/${encodeURIComponent(leadId)}/account-card`,
        { method: 'DELETE' },
        'Failed to unlink account card',
        fetch
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketingLeadsKey });
    },
  });
}
